import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useEffect, useRef, useState } from "react";
import { createTcfModel, prepareTcfString } from "@devowl-wp/cookie-consent-web-client";
import { extendCommonStylesheet } from "@devowl-wp/web-cookie-banner";
import { useBanner } from "../../../contexts/banner.js";
import { useBannerStylesheet } from "../../../hooks/banner/useBannerStylesheet.js";
const SELECT_NONE = -1;
const BannerHistorySelect = ()=>{
    const banner = useBanner();
    const { Select } = useBannerStylesheet().extend(...extendCommonStylesheet);
    const { set, consent, groups, tcf, isGcm, gcmConsent, lazyLoadedDataForSecondView, activeAction, history, fetchHistory, visible, i18n: { historyLabel, historyItemLoadError, historySelectNone } } = banner;
    const [selectedEntry, setSelectedEntry] = useState();
    // Save the current consent and group settings to restore
    const [originalContext, setOriginalContext] = useState({
        consent,
        groups,
        tcf,
        gcmConsent,
        lazyLoadedDataForSecondView
    });
    const updateOriginalContext = ()=>setOriginalContext({
            consent,
            groups,
            tcf,
            gcmConsent,
            lazyLoadedDataForSecondView
        });
    const setInContext = (param)=>{
        let { buttonClicked, tcf, gcmConsent, ...rest } = param;
        const additionalChanges = {
            // In case we previously used non-TCF functionality, do not show the TCF banner
            isTcf: !!tcf,
            tcf: null,
            gcmConsent: []
        };
        if (process.env.IS_TCF === "1" && tcf) {
            Object.assign(additionalChanges, {
                tcf: "gvl" in tcf ? tcf : createTcfModel(tcf)
            });
            if (buttonClicked == null ? void 0 : buttonClicked.startsWith("implicit_")) {
                prepareTcfString(additionalChanges.tcf.model, buttonClicked);
            }
        }
        /* Silence is golden... Until you have PRO Version! */ set({
            ...rest,
            ...additionalChanges
        });
    };
    useEffect(()=>{
        const defaultContext = {
            consent: [],
            groups: [],
            gcmConsent: [],
            lazyLoadedDataForSecondView: undefined
        };
        if (selectedEntry) {
            const { context } = selectedEntry;
            if (context) {
                setInContext(context);
            } else {
                setInContext(defaultContext);
            }
        } else {
            setInContext(defaultContext);
        }
    }, [
        selectedEntry
    ]);
    const fetched = useRef(false);
    useEffect(()=>{
        async function fetch() {
            const result = await fetchHistory();
            updateOriginalContext();
            set({
                history: result
            });
            setSelectedEntry(result[0]);
        }
        // We need to wait for the lazy-loaded data so we can safely save the original context
        if (lazyLoadedDataForSecondView && !fetched.current && activeAction === "history" && visible) {
            fetched.current = true;
            fetch();
        }
    }, [
        lazyLoadedDataForSecondView,
        activeAction,
        visible
    ]);
    useEffect(()=>{
        if (!visible) {
            fetched.current = false;
        }
    }, [
        visible
    ]);
    // If we close the history, restore the original state
    useEffect(()=>{
        return ()=>setInContext(originalContext);
    }, []);
    // History can only contain my data, so we can pick the first entry for the history
    const uuid = selectedEntry == null ? void 0 : selectedEntry.uuid;
    return /*#__PURE__*/ _jsxs(Fragment, {
        children: [
            historyLabel,
            "\xa0",
            /*#__PURE__*/ _jsx(Select, {
                disabled: !(history == null ? void 0 : history.length),
                value: (selectedEntry == null ? void 0 : selectedEntry.id) || SELECT_NONE,
                onChange: (e)=>{
                    const value = +e.target.value;
                    for (const entry of history){
                        const { id } = entry;
                        if (id === value) {
                            setSelectedEntry(entry);
                            break;
                        }
                    }
                },
                children: (history == null ? void 0 : history.length) > 0 ? history.map((param)=>{
                    let { id, isDoNotTrack, isUnblock, isForwarded, created } = param;
                    return /*#__PURE__*/ _jsxs("option", {
                        value: id,
                        children: [
                            new Date(created).toLocaleString(document.documentElement.lang),
                            isDoNotTrack ? " (Do Not Track)" : "",
                            isUnblock ? " (Content Blocker)" : "",
                            isForwarded ? " (Consent Forwarding)" : ""
                        ]
                    }, id);
                }) : /*#__PURE__*/ _jsx("option", {
                    value: SELECT_NONE,
                    children: historySelectNone
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    opacity: 0.5,
                    marginTop: 5
                },
                children: [
                    "UUID: ",
                    uuid || "-"
                ]
            }),
            !(selectedEntry == null ? void 0 : selectedEntry.context) && /*#__PURE__*/ _jsx("div", {
                style: {
                    fontWeight: "bold",
                    marginTop: 5
                },
                children: historyItemLoadError
            })
        ]
    });
};
export { BannerHistorySelect };
